import React, { useMemo } from "react";
import { motion } from "motion/react";
import {
  CheckCircle2,
  Circle,
  AlertCircle,
  Calendar,
  Users,
  FileText,
  MapPin,
  Clock,
} from "lucide-react";

const ChecklistItem = ({ icon: Icon, label, status, description, onClick }) => {
  const statusConfig = {
    complete: {
      icon: CheckCircle2,
      color: "text-emerald-600 dark:text-emerald-400",
      bg: "bg-emerald-50 dark:bg-emerald-950/40",
      border: "border-emerald-200 dark:border-emerald-800",
    },
    incomplete: {
      icon: Circle,
      color: "text-gray-400 dark:text-gray-600",
      bg: "bg-gray-50 dark:bg-gray-900/50",
      border: "border-gray-200 dark:border-gray-700",
    },
    warning: {
      icon: AlertCircle,
      color: "text-amber-600 dark:text-amber-400",
      bg: "bg-amber-50 dark:bg-amber-950/40",
      border: "border-amber-200 dark:border-amber-800",
    },
  };

  const config = statusConfig[status] || statusConfig.incomplete;
  const StatusIcon = config.icon;

  return (
    <motion.div
      whileHover={{ scale: 1.01, x: 4 }}
      className={`flex items-start gap-4 p-4 rounded-2xl border-2 ${config.border} ${config.bg} transition-all cursor-pointer`}
      onClick={onClick}
    >
      <div className="flex-shrink-0">
        <StatusIcon size={24} className={config.color} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <Icon size={18} className="text-gray-600 dark:text-gray-400" />
          <h3 className="font-semibold text-gray-900 dark:text-white">
            {label}
          </h3>
        </div>
        {description && (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {description}
          </p>
        )}
      </div>
    </motion.div>
  );
};

const GameDayChecklist = ({ game, players, team }) => {
  const checklist = useMemo(() => {
    if (!game || !players || !team) return [];

    const items = [];

    // 1. All waivers signed
    const signedWaivers = players.filter((p) => p.liability_waiver_url).length;
    const totalPlayers = players.length;
    items.push({
      id: "waivers",
      icon: FileText,
      label: "Waivers Signed",
      description: `${signedWaivers} of ${totalPlayers} players have signed waivers`,
      status:
        signedWaivers === totalPlayers
          ? "complete"
          : signedWaivers > totalPlayers * 0.7
            ? "warning"
            : "incomplete",
      action: null,
    });

    // 2. Minimum players available
    const minPlayers = 5; // Adjust based on your league rules
    items.push({
      id: "roster",
      icon: Users,
      label: "Roster Ready",
      description: `${totalPlayers} players on roster (minimum ${minPlayers} required)`,
      status: totalPlayers >= minPlayers ? "complete" : "warning",
      action: null,
    });

    // 3. Game details confirmed
    const hasVenue = !!game.court;
    const hasTime = !!game.game_date;
    items.push({
      id: "details",
      icon: MapPin,
      label: "Game Details Confirmed",
      description:
        hasVenue && hasTime
          ? `${game.court} • ${new Date(game.game_date).toLocaleString(
              "en-US",
              {
                weekday: "short",
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
              },
            )}`
          : "Venue or time not confirmed",
      status: hasVenue && hasTime ? "complete" : "warning",
      action: null,
    });

    // 4. Contact info complete
    const playersWithEmail = players.filter((p) => p.email).length;
    items.push({
      id: "contact",
      icon: Users,
      label: "Player Contact Info",
      description: `${playersWithEmail} of ${totalPlayers} players have email addresses`,
      status:
        playersWithEmail === totalPlayers
          ? "complete"
          : playersWithEmail > totalPlayers * 0.8
            ? "warning"
            : "incomplete",
      action: null,
    });

    // 5. Jersey numbers assigned
    const playersWithJerseys = players.filter((p) => p.jersey_number).length;
    items.push({
      id: "jerseys",
      icon: Users,
      label: "Jersey Numbers Assigned",
      description: `${playersWithJerseys} of ${totalPlayers} players have jersey numbers`,
      status:
        playersWithJerseys === totalPlayers
          ? "complete"
          : playersWithJerseys > totalPlayers * 0.7
            ? "warning"
            : "incomplete",
      action: null,
    });

    return items;
  }, [game, players, team]);

  const completionPercentage = useMemo(() => {
    if (checklist.length === 0) return 0;
    const completed = checklist.filter(
      (item) => item.status === "complete",
    ).length;
    return Math.round((completed / checklist.length) * 100);
  }, [checklist]);

  const isReady = completionPercentage === 100;

  if (!game) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 border border-gray-100 dark:border-gray-700 text-center">
        <Calendar className="mx-auto text-gray-400 mb-3" size={48} />
        <p className="text-gray-500 dark:text-gray-400">
          Select an upcoming game to see the game day checklist
        </p>
      </div>
    );
  }

  const opponent =
    game.team1?.id === team?.id ? game.team2?.name : game.team1?.name;
  const gameDate = new Date(game.game_date);
  const daysUntil = Math.ceil((gameDate - new Date()) / (1000 * 60 * 60 * 24));

  return (
    <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 sm:p-8 border border-gray-100 dark:border-gray-700 shadow-lg">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-500/10 dark:bg-blue-500/20">
            <Calendar size={22} className="text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
              Game Day Checklist
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              vs {opponent}
            </p>
          </div>
        </div>

        {/* Game Info Banner */}
        <div
          className="bg-gradient-to-r from-blue-50 to-sky-50 dark:from-blue-950/40 dark:to-sky-950/30 
                       rounded-2xl p-4 border-2 border-blue-200 dark:border-blue-800"
        >
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Clock className="text-blue-600 dark:text-blue-400" size={16} />
              <span className="font-semibold text-gray-900 dark:text-white">
                {daysUntil === 0
                  ? "Today"
                  : daysUntil === 1
                    ? "Tomorrow"
                    : `${daysUntil} days`}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar
                className="text-blue-600 dark:text-blue-400"
                size={16}
              />
              <span className="text-gray-700 dark:text-gray-300">
                {gameDate.toLocaleDateString("en-US", {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                })}
              </span>
            </div>
            {game.court && (
              <div className="flex items-center gap-2">
                <MapPin
                  className="text-blue-600 dark:text-blue-400"
                  size={16}
                />
                <span className="text-gray-700 dark:text-gray-300">
                  {game.court}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            Completion Progress
          </span>
          <span
            className={`text-sm font-bold ${
              isReady
                ? "text-emerald-600 dark:text-emerald-400"
                : "text-gray-600 dark:text-gray-400"
            }`}
          >
            {completionPercentage}%
          </span>
        </div>
        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${completionPercentage}%` }}
            transition={{ duration: 1, ease: "easeOut" }}
            className={`h-full rounded-full ${
              isReady
                ? "bg-gradient-to-r from-emerald-500 to-green-500"
                : "bg-gradient-to-r from-blue-500 to-sky-500"
            }`}
          />
        </div>
      </div>

      {/* Ready Status */}
      {isReady && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-950/40 dark:to-green-950/30 
                     rounded-2xl p-4 border-2 border-emerald-200 dark:border-emerald-800"
        >
          <div className="flex items-center gap-3">
            <CheckCircle2
              className="text-emerald-600 dark:text-emerald-400"
              size={24}
            />
            <div>
              <p className="font-bold text-emerald-900 dark:text-emerald-100">
                All Set! 🎉
              </p>
              <p className="text-sm text-emerald-700 dark:text-emerald-300">
                Your team is ready for game day
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Checklist Items */}
      <div className="space-y-3">
        {checklist.map((item, index) => (
          <motion.div
            key={item.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <ChecklistItem {...item} />
          </motion.div>
        ))}
      </div>

      {/* Quick Actions */}
      {!isReady && (
        <div className="mt-6 pt-6 border-t dark:border-gray-700">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            Quick Actions:
          </p>
          <div className="flex flex-wrap gap-2">
            {checklist
              .filter((item) => item.status !== "complete")
              .map((item) => (
                <button
                  key={item.id}
                  className="px-4 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 
                           rounded-xl text-sm font-semibold hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                >
                  Fix {item.label}
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GameDayChecklist;
